import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import useReaderStore from "../../context/readerStore";
import { useIsMobile } from "../../hooks/useMediaQuery";
import {
  getPdfOutline,
  sanitizeOutlineTitle,
} from "../../services/pdfService";
import "./TableOfContents.css";



const buildMetadataPdfToc = (tableOfContents) => {
  if (!Array.isArray(tableOfContents)) return [];

  return tableOfContents
    .map((entry) => {
      const startPage = Number(entry?.start_page) || null;
      const endPage = Number(entry?.end_page) || startPage || null;
      return {
        title: sanitizeOutlineTitle(entry?.title || ""),
        pageNumber: startPage,
        startPage,
        endPage,
        index: entry?.index ?? null,
        type: entry?.type || null,
      };
    })
    .filter((entry) => entry.title);
};


const TableOfContents = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    toc,
    setCurrentPage,
    document,
    documentType,
    setToc,
    toggleTocSidebar,
    metadata,
    currentPage,
    isChunked,
  } = useReaderStore();


  let activeEpubChapterNumber = null;
  if (
    documentType === "epub" &&
    metadata &&
    Array.isArray(metadata.chapters) &&
    currentPage
  ) {
    const activeChapter = metadata.chapters.find((chapter) => {
      const start = Number(chapter.start_page) || 1;
      const end = Number(chapter.end_page) || start;
      const page = Number(currentPage) || 1;
      return page >= start && page <= end;
    });
    if (activeChapter && activeChapter.chapter_number != null) {
      activeEpubChapterNumber = Number(activeChapter.chapter_number);
    }
  }

  const navigateToPdfPage = (page) => {
    const targetPage = Number(page);
    if (!targetPage) return;
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("reader:go-to-page", { detail: { page: targetPage } }),
      );
    }
  };

  const handleTocItemClick = (item) => {
    const directPage = item.page || item.pageNumber || item.startPage;
    if (!directPage) return;

    const targetPage = Number(directPage);
    if (!targetPage) return;

    if (documentType === "pdf") {
      setCurrentPage(targetPage);
      navigateToPdfPage(targetPage);
    } else {
      setCurrentPage(targetPage);
    }
    if (isMobile) toggleTocSidebar();
  };


  useEffect(() => {
    const loadToc = async () => {
      if (
        documentType === "epub" &&
        metadata &&
        Array.isArray(metadata.chapters)
      ) {
        const epubToc = metadata.chapters.map((chapter) => {
          const chapterNumber = Number(chapter.chapter_number) || 0;
          const startPage = Number(chapter.start_page) || 1;
          return {
            title:
              sanitizeOutlineTitle(chapter.title) ||
              (chapterNumber
                ? `${t("toc.chapter")} ${chapterNumber}`
                : t("toc.untitled")),
            pageNumber: startPage,
            chapterNumber,
          };
        });
        setToc(epubToc);
      } else if (documentType === "pdf" && document) {
        try {
          const metadataToc = buildMetadataPdfToc(metadata?.table_of_contents);
          if (metadataToc.length > 0) {
            setToc(metadataToc);
          } else if (!isChunked) {
            // Only use PDF.js embedded outline for non-chunked PDFs where all
            // pages are available for destination resolution. For chunked PDFs,
            // the embedded outline references can't be resolved against the
            // split chunk files, leading to non-functional TOC items.
            const pdfToc = await getPdfOutline(document);
            setToc(pdfToc);
          } else {
            // Chunked PDF without rich metadata – show empty TOC rather than
            // misleading items that can never navigate.
            setToc([]);
          }
        } catch (error) {
          console.error("Error loading PDF TOC:", error);
          setToc([]);
        }
      } else {
        setToc([]);
      }
    };

    loadToc();
  }, [document, documentType, isChunked, metadata, setToc, t]);


  const renderTocItems = (items, depth = 0) => {
    return items.map((item, index) => {
      const isNavigatable = !!(item.page || item.pageNumber);

      let isActive = false;
      if (documentType === "epub") {
        if (item.chapterNumber && activeEpubChapterNumber != null) {
          isActive =
            Number(item.chapterNumber) === Number(activeEpubChapterNumber);
        } else if (item.pageNumber && currentPage) {
          isActive = Number(item.pageNumber) === Number(currentPage);
        }
      } else if (documentType === "pdf") {
        if (item.startPage && item.endPage) {
          const page = Number(currentPage) || 1;
          isActive =
            page >= Number(item.startPage) && page <= Number(item.endPage);
        } else if (item.page || item.pageNumber) {
          const targetPage = item.page || item.pageNumber;
          isActive = Number(targetPage) === Number(currentPage);
        }
      }

      return (
        <div
          key={index}
          className={`toc-item ${isActive ? "active" : ""}`}
          style={{ "--toc-depth": depth }}
        >
          <button
            className={`toc-item-title ${
              !isNavigatable ? "disabled" : ""
            } ${isActive ? "active" : ""}`}
            onClick={() => isNavigatable && handleTocItemClick(item)}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && isNavigatable) {
                e.preventDefault();
                handleTocItemClick(item);
              }
            }}
            title={item.href || item.title}
            type="button"
            aria-current={isActive ? "page" : undefined}
            disabled={!isNavigatable}
          >
            {item.title || t("toc.untitled")}
          </button>

          {(item.children || item.items) &&
            (item.children || item.items).length > 0 && (
              <div className="toc-item-children">
                {renderTocItems(item.children || item.items, depth + 1)}
              </div>
            )}
        </div>
      );
    });
  };

  return (
    <div
      className="table-of-contents open"
      role="navigation"
      aria-label={t("toc.title")}
    >
      <div className="toc-header">
        <div className="toc-header-leading" aria-hidden="true" />
        <h3 tabIndex="-1">{t("toc.title")}</h3>
        <button
          type="button"
          className="toc-close-button"
          onClick={toggleTocSidebar}
          aria-label={t("toc.close")}
          title={t("toc.close")}
        >
          <CloseIcon width={20} height={20} aria-hidden="true" />
        </button>
      </div>

      <div className="toc-content">
        {toc.length > 0 ? (
          <div className="toc-container">{renderTocItems(toc)}</div>
        ) : (
          <div className="toc-empty">
            <p>{t("toc.noItems")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableOfContents;
